import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Card, CardHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Input } from '@/components/Input';
import { api, ApiError } from '@/lib/api';
import { useMe } from '@/lib/auth';

const schema = z.object({
  subject: z.string().min(1, 'Subject is required'),
  message: z.string().min(10, 'Tell us a bit more'),
});

type FormValues = z.infer<typeof schema>;

export function SupportTab() {
  const me = useMe();
  const send = useMutation({
    mutationFn: (input: FormValues) =>
      api.post<{ message: string }>('/api/Mails', {
        ...input,
        email: me.data?.email ?? '',
        name: me.data?.name ?? me.data?.email ?? '',
      }),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { subject: '', message: '' },
  });

  const onSubmit = handleSubmit((values) => {
    send.mutate(values, { onSuccess: () => reset() });
  });

  return (
    <Card>
      <CardHeader
        title="Contact support"
        description="Tell us what's going on and we'll get back within one business day."
      />
      <form onSubmit={onSubmit} className="flex flex-col gap-gap-element">
        <Field id="subject" label="Subject" error={errors.subject?.message}>
          <Input id="subject" {...register('subject')} />
        </Field>
        <Field id="message" label="Message" error={errors.message?.message}>
          <textarea
            id="message"
            rows={6}
            className="w-full rounded-md border border-border bg-surface p-3 text-body text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            {...register('message')}
          />
        </Field>

        {send.isSuccess ? (
          <div className="rounded-md border border-success/40 bg-success/5 px-4 py-3 text-body-sm text-success">
            Message sent. We'll be in touch shortly.
          </div>
        ) : null}
        {send.error ? (
          <div className="rounded-md border border-danger/40 bg-danger/5 px-4 py-3 text-body-sm text-danger">
            {send.error instanceof ApiError
              ? send.error.message
              : 'Could not send message.'}
          </div>
        ) : null}

        <div className="flex justify-end">
          <Button type="submit" disabled={send.isPending}>
            {send.isPending ? 'Sending…' : 'Send message'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
